const BASE = import.meta.env.VITE_API_URL ?? 'https://api.fformkit.com'

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    // credentials: 'include' sends the HttpOnly session cookie on every request,
    // replacing the previous localStorage-based Bearer token approach.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(err.error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

// Auth
export const api = {
  auth: {
    register: (data: { email: string; password: string; name: string }) =>
      request<{ token: string; user: User }>('/api/v1/auth/register', {
        method: 'POST', body: JSON.stringify(data)
      }),
    login: (data: { email: string; password: string }) =>
      request<{ token: string; user: User }>('/api/v1/auth/login', {
        method: 'POST', body: JSON.stringify(data)
      }),
    logout: () =>
      request<{ success: boolean }>('/api/v1/auth/logout', { method: 'POST' }),
    me: () => request<User>('/api/v1/me'),
    forgotPassword: (email: string) =>
      request<{ success: boolean }>('/api/v1/auth/forgot-password', {
        method: 'POST', body: JSON.stringify({ email }),
      }),
    resetPassword: (token: string, password: string) =>
      request<{ success: boolean }>('/api/v1/auth/reset-password', {
        method: 'POST', body: JSON.stringify({ token, password }),
      }),
  },

  projects: {
    list: () => request<{
      projects: ProjectWithStats[]
      plan_limit: number | null
      used: number
      submissions_limit: number
      submissions_used: number
      storage_limit_bytes: number
      storage_used_bytes: number
      plan_features: import('./plans').PlanFeatures
    }>('/api/v1/projects'),
    create: (data: { name: string }) =>
      request<Project>('/api/v1/projects', { method: 'POST', body: JSON.stringify(data) }),
    get: (id: string) => request<ProjectWithStats>(`/api/v1/projects/${id}`),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/v1/projects/${id}`, { method: 'DELETE' }),
    rotateKey: (id: string) =>
      request<{ api_key: string; api_key_hint: string }>(`/api/v1/projects/${id}/rotate-key`, { method: 'POST' }),
    updateSettings: (id: string, data: { allowed_bundle_ids?: string[]; name?: string }) =>
      request<Project>(`/api/v1/projects/${id}/settings`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  feedback: {
    list: (projectId: string, params?: FeedbackParams) => {
      const qs = new URLSearchParams(params as Record<string, string>).toString()
      return request<PaginatedFeedback>(`/api/v1/projects/${projectId}/feedback?${qs}`)
    },
    listAll: (params?: AllFeedbackParams) => {
      const filtered = Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== ''))
      const qs = new URLSearchParams(filtered as Record<string, string>).toString()
      return request<PaginatedFeedback>(`/api/v1/feedback?${qs}`)
    },
    get: (id: string) => request<Feedback>(`/api/v1/feedback/${id}`),
    update: (id: string, data: Partial<Feedback>) =>
      request<Feedback>(`/api/v1/feedback/${id}`, {
        method: 'PUT', body: JSON.stringify(data)
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/v1/feedback/${id}`, { method: 'DELETE' }),
    export: async (params?: AllFeedbackParams): Promise<Blob> => {
      const filtered = Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== ''))
      const qs = new URLSearchParams(filtered as Record<string, string>).toString()
      const res = await fetch(`${BASE}/api/v1/feedback/export?${qs}`, { credentials: 'include' })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
      }
      return res.blob()
    },
    stats: (projectId: string) => request<Stats>(`/api/v1/projects/${projectId}/stats`),
  },

  formConfig: {
    get: (projectId: string) => request<FormConfig>(`/api/v1/projects/${projectId}/form-config`),
    update: (projectId: string, data: Partial<FormConfig>) =>
      request<FormConfig>(`/api/v1/projects/${projectId}/form-config`, {
        method: 'PUT', body: JSON.stringify(data)
      }),
  },

  user: {
    updateProfile: (data: { name?: string; email?: string }) =>
      request<User>('/api/v1/me', { method: 'PUT', body: JSON.stringify(data) }),
    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      request<{ success: boolean }>('/api/v1/me/password', { method: 'PUT', body: JSON.stringify(data) }),
    deleteAccount: () =>
      request<{ success: boolean }>('/api/v1/me', { method: 'DELETE' }),
    getNotifications: () =>
      request<NotifPrefs>('/api/v1/me/notifications'),
    updateNotifications: (data: NotifPrefs) =>
      request<{ success: boolean }>('/api/v1/me/notifications', { method: 'PUT', body: JSON.stringify(data) }),
  },

  billing: {
    createCheckout: (plan: 'launch' | 'pro' | 'studio', billing: 'monthly' | 'annual' = 'monthly') =>
      request<{ checkout_url: string }>('/api/v1/billing/checkout', {
        method: 'POST', body: JSON.stringify({ plan, billing }),
      }),
    getSubscription: () =>
      request<{ subscription: DodoSubscription | null }>('/api/v1/billing/subscription'),
    getHistory: (pageSize = 10, pageToken?: string) => {
      const qs = new URLSearchParams({ page_size: String(pageSize) })
      if (pageToken) qs.set('page_token', pageToken)
      return request<{ payments: DodoPayment[]; next_key: string | null }>(`/api/v1/billing/history?${qs}`)
    },
    cancel: () =>
      request<{ success: boolean }>('/api/v1/billing/cancel', { method: 'POST' }),
  },

  support: {
    getConfig: () => request<Record<string, unknown>>('/api/v1/support/config'),
    submit: (data: { message: string; rating?: number; email?: string }) =>
      request<{ id: string }>('/api/v1/support/submit', {
        method: 'POST', body: JSON.stringify(data),
      }),
  },

  admin: {
    stats: () => request<AdminStats>('/api/v1/admin/stats'),
    users: (params?: { search?: string; plan?: string; page?: string }) => {
      const filtered = Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined))
      const qs = new URLSearchParams(filtered as Record<string, string>).toString()
      return request<AdminUsersResponse>(`/api/v1/admin/users?${qs}`)
    },
    getUser: (id: string) => request<AdminUserDetail>(`/api/v1/admin/users/${id}`),
    updatePlan: (id: string, plan: string) =>
      request<{ success: boolean; plan: string }>(`/api/v1/admin/users/${id}/plan`, {
        method: 'PUT', body: JSON.stringify({ plan }),
      }),
    deleteUser: (id: string) =>
      request<{ success: boolean }>(`/api/v1/admin/users/${id}`, { method: 'DELETE' }),
  },
}

// Types
export interface NotifPrefs {
  email: string
  digest: string
  spike: boolean
  usage: boolean
  weekly: boolean
}

export interface User {
  id: string; email: string; name: string
  plan: 'free' | 'launch' | 'pro' | 'studio'
  plan_interval?: 'monthly' | 'annual'
  dodo_subscription_id?: string | null
  created_at: number
}

export interface DodoSubscription {
  subscription_id?: string
  status?: string
  next_billing_date?: string
  cancelled_at?: string
  cancel_at_next_billing_date?: boolean
  recurring_pre_tax_amount?: number
  currency?: string
  created_at?: string
  is_annual?: boolean
  [key: string]: unknown
}

export interface DodoPayment {
  payment_id?: string
  total_amount?: number
  currency?: string
  status?: string
  created_at?: string
  payment_link?: string
  invoice_url?: string
  [key: string]: unknown
}

export interface Project {
  id: string; user_id: string; name: string
  /** Full key (legacy rows) or display hint fb_live_****xxxx (new rows). */
  api_key: string
  /** Hint is set for new/rotated keys — if null, api_key is still the full plaintext key. */
  api_key_hint: string | null
  /** JSON array of allowed bundle IDs. Null = allow all. */
  allowed_bundle_ids: string | null
  created_at: number
}

export interface ProjectWithStats extends Project {
  feedback_count: number; open_count: number; week_count: number
}

export interface Feedback {
  id: string; project_id: string; message: string
  rating: number | null; email: string | null
  screenshot_key: string | null; screenshot_url?: string
  tag: 'bug' | 'feature' | 'praise' | 'performance' | 'other' | null
  status: 'open' | 'resolved'; priority: number
  internal_note: string | null
  device_model: string | null; os_version: string | null
  app_version: string | null; platform: string | null
  created_at: number
  project_name?: string
  repeat_count?: number
}

export interface PaginatedFeedback {
  data: Feedback[]
  pagination: { page: number; limit: number; total: number; pages: number }
}

export interface FeedbackParams {
  page?: string; limit?: string; status?: string
  tag?: string; platform?: string; search?: string
}

export interface AllFeedbackParams {
  page?: string; limit?: string; status?: string
  tag?: string; search?: string; projects?: string
}

export interface FormConfig {
  id: string; project_id: string
  primary_color: string; background_color: string
  title_color: string; star_color_active: string
  star_color_inactive: string; input_background_color: string
  input_border_color: string; placeholder_color: string; button_text_color: string; border_radius: number
  button_label: string; form_title: string; placeholder_text: string
  success_message: string; success_color: string
  email_placeholder: string; screenshot_label: string; screenshot_icon: string
  tagline: string; tagline_color: string
  show_rating: boolean; show_screenshot: boolean; show_email: boolean
}

export interface AdminUserRow {
  id: string; email: string; name: string; plan: string; created_at: number
  project_count: number; total_submissions: number; monthly_submissions: number
}

export interface AdminStats {
  users: { total: number; free_count: number; launch_count: number; pro_count: number; studio_count: number }
  projects: { total: number }
  feedback: { total: number; this_month: number; this_week: number; today: number }
  plans: { plan: string; count: number }[]
  daily: { date: string; count: number }[]
  tags: { tag: string; count: number }[]
}

export interface AdminUsersResponse {
  users: AdminUserRow[]
  pagination: { page: number; limit: number; total: number; pages: number }
}

export interface AdminUserDetail {
  user: AdminUserRow & { dodo_subscription_id: string | null }
  projects: {
    id: string; name: string; platform: string; environment: string; created_at: number
    feedback_count: number; open_count: number; monthly_count: number
  }[]
  recentFeedback: {
    id: string; message: string; rating: number | null
    tag: string | null; status: string; created_at: number; project_name: string
  }[]
}

export interface Stats {
  totals: {
    total: number; open_count: number; resolved_count: number
    today_count: number; hour_count: number; avg_rating: number | null
  }
  daily: { date: string; count: number }[]
  topIssues: { id: string; message: string; tag: string | null; created_at: number }[]
}
